import { useEffect, useState } from "react";
import api, { inr, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Receipt } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  "Vegetables", "Milk & Dairy", "Meat & Poultry", "Groceries",
  "Gas / LPG", "Electricity", "Water", "Rent", "Salary",
  "Repair & Maintenance", "Marketing", "Stationery", "Misc",
];

const today = () => new Date().toISOString().slice(0, 10);

export default function Expenses() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [start, setStart] = useState(today());
  const [end, setEnd] = useState(today());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: today(), category: "Vegetables", amount: 0, paid_to: "", mode: "cash", notes: "" });

  const reload = async () => {
    const { data } = await api.get("/expenses", { params: { start_date: start, end_date: end } });
    setRows(data.rows || []);
    setTotal(data.total || 0);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [start, end]);

  const save = async () => {
    try {
      await api.post("/expenses", { ...form, amount: Number(form.amount || 0) });
      toast.success("Expense recorded");
      setOpen(false);
      setForm({ date: today(), category: "Vegetables", amount: 0, paid_to: "", mode: "cash", notes: "" });
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await api.delete(`/expenses/${id}`); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 sm:p-8" data-testid="expenses-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-[#2D2C2A]">Daily Expenses</h1>
          <p className="text-sm text-[#7A7873] mt-1">Petty cash — vegetable buys, milk, gas, repairs etc. Deducted in shift close.</p>
        </div>
        <Button className="bg-[#D95D39] hover:bg-[#C24F2C]" onClick={() => setOpen(true)} data-testid="add-expense-btn">
          <Plus size={16} className="mr-1.5" /> Add Expense
        </Button>
      </div>

      <div className="flex items-end gap-3 mb-4 flex-wrap">
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" />
        </div>
        <div className="ml-auto bg-[#FFF4E0] border border-[#E9A23B] rounded-md px-4 py-2">
          <div className="text-[10px] uppercase tracking-wider text-[#8B6B1F]">Total Spent</div>
          <div className="font-mono-num font-bold text-lg text-[#E9A23B]" data-testid="expense-total">{inr(total)}</div>
        </div>
      </div>

      <div className="bg-white border border-[#EBE8E2] rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-[#F4F2EE] text-[#7A7873] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">Date</th>
              <th className="text-left px-4 py-2.5">Category</th>
              <th className="text-left px-4 py-2.5">Paid To</th>
              <th className="text-left px-4 py-2.5">Mode</th>
              <th className="text-right px-4 py-2.5">Amount</th>
              <th className="text-left px-4 py-2.5">Notes</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#EBE8E2]" data-testid={`expense-${r.id}`}>
                <td className="px-4 py-2.5 font-mono-num">{r.date}</td>
                <td className="px-4 py-2.5">{r.category}</td>
                <td className="px-4 py-2.5">{r.paid_to || "—"}</td>
                <td className="px-4 py-2.5 capitalize">{r.mode}</td>
                <td className="px-4 py-2.5 text-right font-mono-num font-semibold">{inr(r.amount)}</td>
                <td className="px-4 py-2.5 text-xs text-[#7A7873]">{r.notes || "—"}</td>
                <td className="px-4 py-2.5 text-right">
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-[#E76F51]" onClick={() => del(r.id)}>
                    <Trash2 size={13} />
                  </Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-[#7A7873]">
                <Receipt size={24} className="mx-auto mb-2 opacity-50" />
                No expenses in this range.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Add Expense</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amount (₹)</Label>
                <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount-input" />
              </div>
              <div>
                <Label>Mode</Label>
                <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="bank">Bank</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Paid To (vendor name)</Label>
              <Input value={form.paid_to} onChange={(e) => setForm({ ...form, paid_to: e.target.value })} placeholder="e.g. Sabzi Mandi" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#D95D39] hover:bg-[#C24F2C]" onClick={save} data-testid="save-expense">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
